import type { StandardTask } from '@seen/core/models/tasks/standard/standardTask'
import { cn } from '@seen/ui'
import { IconAlertCircle } from '@tabler/icons-react'
import type { JSX, ReactNode } from 'react'
import { ErrorBoundary } from 'react-error-boundary'

export interface TaskRowErrorBoundaryProps {
  task: StandardTask
  hideCheckbox?: boolean
  prefix?: ReactNode
  className?: string
  children: ReactNode
}

// Task lists render many rows at once, and a single malformed Task resource
// (for example one missing a field a model getter asserts on) would otherwise
// bubble up to the page-level boundary and blank the whole list. Contain the
// failure to the row so every other task stays usable.
export function TaskRowErrorBoundary({
  task,
  hideCheckbox,
  prefix,
  className,
  children,
}: TaskRowErrorBoundaryProps): JSX.Element {
  return (
    <ErrorBoundary
      resetKeys={[task]}
      fallbackRender={({ error }) => (
        <TaskRowErrorFallback
          task={task}
          error={error}
          hideCheckbox={hideCheckbox}
          prefix={prefix}
          className={className}
        />
      )}
    >
      {children}
    </ErrorBoundary>
  )
}

// Mirrors the TaskRow skeleton (checkbox gutter, priority and state icon
// slots) so the message lines up with the titles of the surrounding rows.
function TaskRowErrorFallback({
  task,
  error,
  hideCheckbox,
  prefix,
  className,
}: {
  task: StandardTask
  error: unknown
  hideCheckbox?: boolean
  prefix?: ReactNode
  className?: string
}): JSX.Element {
  const message = error instanceof Error ? error.message : String(error)
  return (
    <div
      role="alert"
      title={message}
      className={cn(
        'flex min-h-10 items-center bg-destructive/10 px-pagex-default lg:px-pagex-lg',
        !hideCheckbox && 'pr-8 pl-0 lg:pr-8 lg:pl-0',
        className
      )}
    >
      {!hideCheckbox && <div className="w-8 max-w-8 min-w-8 self-stretch" />}
      {prefix}
      <div className="flex flex-1 items-center gap-2">
        <div className="size-4 shrink-0" />
        <IconAlertCircle className="size-4 shrink-0 text-destructive" />
        <span className="line-clamp-1 text-sm font-medium text-foreground-secondary">
          This task couldn't be displayed ({task.reference.reference})
        </span>
      </div>
    </div>
  )
}
